import {
  BarRaiserTechDomains,
  ContentUserRole,
  UserProfile,
  UserRoles,
} from '@amzn/awscat-pattern-library-service-model';

import type { LoadedDocumentKind } from '../model/LoadedDocument';
import { UserNotLoggedInError } from './Errors';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';
import { log } from '../loggers/Logger';

export type ActiveRole = 'author' | 'bar_raiser' | 'admin';

export type UserServiceEvents = {
  changed: () => void;
  roleChanged: (role: ActiveRole) => void;
};

const STORAGE_KEY = 'apgl-active-role';
const VALID_ROLES: readonly ActiveRole[] = ['author', 'bar_raiser', 'admin'];

// If the user has not chosen a role, one of these role will be automatically
// chosen for them. If the user has multiple roles, the one appear first will
// be chosen first.
const DEFAULT_ROLE_PRECEDENCE: readonly ActiveRole[] = ['bar_raiser', 'admin'];

export class UserService {
  private readonly _emitter = new TypedEmitter<UserServiceEvents>();
  private _userProfile: UserProfile | undefined;
  private _availableRoles: readonly ActiveRole[] = [];
  private _activeRole: ActiveRole = 'author';
  private _hasStagedUserProfile = false;

  /** Replaces the current user profile, re-derives roles, and notifies listeners. */
  setUserProfile(userProfile: UserProfile): void {
    this.stageUserProfile(userProfile);
    this.notifyStagedUserProfile();
  }

  /**
   * Replaces the current user profile and re-derives roles without notifying
   * listeners; use during a React render, then call
   * {@link notifyStagedUserProfile} once the render commits.
   */
  stageUserProfile(userProfile: UserProfile): void {
    if (this._userProfile === userProfile) return;

    this._userProfile = userProfile;
    this._availableRoles = deriveAvailableRoles(userProfile);
    this._activeRole = this._resolveInitialRole();
    this._hasStagedUserProfile = true;
  }

  /** Notifies listeners of a profile staged by {@link stageUserProfile}; does nothing when none is pending. */
  notifyStagedUserProfile(): void {
    if (!this._hasStagedUserProfile) return;

    this._hasStagedUserProfile = false;
    this._emitter.emit('roleChanged', this._activeRole);
    this._emitter.emit('changed');
  }

  /**
   * The current user profile.
   * @throws UserNotLoggedInError when no profile has been set.
   */
  get userProfile(): UserProfile {
    if (!this._userProfile) {
      throw new UserNotLoggedInError();
    }
    return this._userProfile;
  }

  /** @throws UserNotLoggedInError when no profile has been set. */
  get userId(): string {
    return this.userProfile.userId;
  }

  /** @throws UserNotLoggedInError when no profile has been set. */
  get name(): string {
    return this.userProfile.name;
  }

  /** @throws UserNotLoggedInError when no profile has been set. */
  get email(): string {
    return this.userProfile.email;
  }

  /** @throws UserNotLoggedInError when no profile has been set. */
  get roles(): ReadonlySet<UserRoles> {
    return this.userProfile.roles ?? new Set();
  }

  get activeRole(): ActiveRole {
    return this._activeRole;
  }

  get availableRoles(): readonly ActiveRole[] {
    return this._availableRoles;
  }

  /**
   * Whether the user may perform bar raiser actions (evaluation, publisher-review
   * approvals) on content in the given tech domains. Admins can always act; bar
   * raisers need the Bar raiser hat active AND a BarRaiser content role whose
   * domain matches the content — a Databases bar raiser cannot act on Serverless
   * content, and the Bar raiser role alone is never enough.
   */
  canActAsBarRaiserForContent(
    mainTechDomain: string | undefined,
    techDomains: readonly string[] | undefined,
  ): boolean {
    if (!this._userProfile) return false;
    if (this._activeRole === 'admin') return true;
    if (this._activeRole !== 'bar_raiser') return false;
    return hasBarRaiserRoleForContent(this._userProfile, mainTechDomain, techDomains);
  }

  /**
   * Whether the user may start a new inline comment thread on the given content.
   * Requires a draft, because a comment is feedback the author still has to act
   * on and finalizing or published documents have no draft left to change, plus
   * either the Admin hat or a bar raiser role matching the content's tech
   * domains. Authors and co-authors may read existing threads but never open
   * one, so reading is deliberately not gated by this.
   */
  canStartCommentThreadForContent(
    documentKind: LoadedDocumentKind,
    mainTechDomain: string | undefined,
    techDomains: readonly string[] | undefined,
  ): boolean {
    if (documentKind !== 'draft') return false;
    if (this._activeRole === 'admin') return true;
    return this.canActAsBarRaiserForContent(mainTechDomain, techDomains);
  }

  /**
   * Whether the user may see a code repository's AppSec and open-source review
   * ticket links. Visible to Admins, Publishers, Tech writers, any bar raiser,
   * the content owner and co-authors, so reviewers can verify the security gate
   * themselves; hidden from general readers. Display-only: ticket urls are still
   * present in the content payload, so this is not an access control.
   */
  canSeeCodeReviewTicketsForContent(
    ownerId: string | undefined,
    coAuthorIds: readonly string[],
  ): boolean {
    return this._isContentAuthorOrReviewer(ownerId, coAuthorIds);
  }

  /**
   * Whether the user may see a published version's activity (workflow event
   * history). Visible to Admins, Publishers, Tech writers, any bar raiser, the
   * content owner and co-authors; hidden from general readers. Display-only:
   * the backend enforces its own access check on the events.
   */
  canSeeActivityForContent(ownerId: string | undefined, coAuthorIds: readonly string[]): boolean {
    return this._isContentAuthorOrReviewer(ownerId, coAuthorIds);
  }

  /**
   * True for the content owner or a co-author, or a holder of the Admin,
   * Publisher, or Tech writer role, or any bar raiser content role — regardless
   * of the active hat.
   */
  private _isContentAuthorOrReviewer(
    ownerId: string | undefined,
    coAuthorIds: readonly string[],
  ): boolean {
    if (!this._userProfile) return false;
    const { userId, roles, contentRoles } = this._userProfile;
    if (
      roles?.has(UserRoles.Admin) ||
      roles?.has(UserRoles.Publisher) ||
      roles?.has(UserRoles.TechWriter)
    ) {
      return true;
    }
    if ((contentRoles ?? []).some((cr) => cr.contentRole === ContentUserRole.BarRaiser)) {
      return true;
    }
    return userId === ownerId || coAuthorIds.includes(userId);
  }

  setActiveRole(role: ActiveRole): void {
    if (!this._availableRoles.includes(role)) return;
    if (role === this._activeRole) return;
    this._activeRole = role;
    persistRole(role);
    this._emitter.emit('roleChanged', role);
  }

  on<K extends keyof UserServiceEvents & string>(
    event: K,
    listener: UserServiceEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  private _resolveInitialRole(): ActiveRole {
    // Restore the previously stored role if exists
    const persisted = getPersistedRole();
    if (persisted) {
      return this._availableRoles.includes(persisted) ? persisted : 'author';
    }

    // At this point, we never had a role chosen before.
    // Automatically choose the default role in order of preference, because
    // users always forget to choose a role.
    const autoPicked = DEFAULT_ROLE_PRECEDENCE.find((role) => this._availableRoles.includes(role));
    if (!autoPicked) return 'author';

    persistRole(autoPicked);
    return autoPicked;
  }
}

function deriveAvailableRoles(userProfile: UserProfile): ActiveRole[] {
  const roles: ActiveRole[] = ['author'];

  // Bar raiser hat is unlocked by holding any BarRaiser content role, since
  // publisher-review actions are gated on bar-raiser eligibility.
  const isBarRaiser = (userProfile.contentRoles ?? []).some(
    (cr) => cr.contentRole === ContentUserRole.BarRaiser,
  );
  if (isBarRaiser) roles.push('bar_raiser');

  // Admin hat is gated on the global Admin user role.
  if (userProfile.roles?.has(UserRoles.Admin)) roles.push('admin');

  return roles;
}

/**
 * Whether the user holds a BarRaiser content role whose tech-domain filter
 * matches the given content. A role filtering on `mainTechDomain` must match the
 * content's main tech domain; a role filtering on `techDomains` must match one
 * of the content's secondary tech domains.
 */
function hasBarRaiserRoleForContent(
  userProfile: UserProfile,
  mainTechDomain: string | undefined,
  techDomains: readonly string[] | undefined,
): boolean {
  if (!Array.isArray(userProfile.contentRoles)) return false;

  return userProfile.contentRoles.some((contentRole) => {
    if (contentRole.contentRole !== ContentUserRole.BarRaiser) return false;

    const name = contentRole.filter?.metadataAttributeName;
    const value = contentRole.filter?.metadataAttributeValue;
    // The filter must target a bar-raiser tech domain to be meaningful.
    if (value === undefined || !BarRaiserTechDomains.includes(value)) return false;

    // Match the filter's scope against the content's own tech domains.
    if (name === 'mainTechDomain') return value === mainTechDomain;
    if (name === 'techDomains') return techDomains?.includes(value) ?? false;
    return false;
  });
}

function getPersistedRole(): ActiveRole | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_ROLES.includes(stored as ActiveRole)) {
      return stored as ActiveRole;
    }
  } catch (err) {
    log.info({ error: err }, 'localStorage unavailable — cannot restore persisted role');
  }
  return null;
}

function persistRole(role: ActiveRole): void {
  try {
    localStorage.setItem(STORAGE_KEY, role);
  } catch (err) {
    log.warn({ error: err }, 'localStorage unavailable — cannot persist role');
  }
}
